import "./styles.scss"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import { Button, CircularProgress, TextField } from "@material-ui/core"
import { getUser } from "../../actions"
import { useBadges, useCommunities, useEvents, useGames, useUsers } from "./hooks"
import { List, Tabs, TABS, AddUserModal, AddCommunityModal } from "./components"
import { IconButton, Modal } from "@material-ui/core"
import { Add } from "@material-ui/icons"

export default function Home() {
  const history = useHistory()
  const [searchText, setSearchText] = useState<string>("")
  const [selectedTab, setTab] = useState<TABS>(TABS.USERS)
  const [addUserModal, setAddUserModal] = useState<boolean>(false)
  const [addCommunityModal, setAddCommunityModal] = useState<boolean>(false)
  const [users, searchUsers, isLoadingSearchUsers] = useUsers()
  const [communities, searchCommunities, isLoadingSearchCommunities] = useCommunities()
  const [events, searchEvents, isLoadingSearchEvents] = useEvents()
  const [badges, searchBadges, isLoadingSearchBadges] = useBadges()
  const [games, searchGames, isLoadingSearchGames] = useGames()
  const isLoading =
    isLoadingSearchEvents ||
    isLoadingSearchCommunities ||
    isLoadingSearchUsers ||
    isLoadingSearchBadges ||
    isLoadingSearchGames

  const fetchCurrentUser = async () => {
    const user = await getUser()
    if (!user?.admin) {
      history.push("/login")
    }
  }

  const data = useMemo(() => {
    switch (selectedTab) {
      case TABS.USERS:
        return users
      case TABS.EVENTS:
        return events
      case TABS.COMMUNITIES:
        return communities
      case TABS.BADGES:
        return badges
      case TABS.GAMES:
        return games
    }
  }, [selectedTab, users, events, communities, badges, games])
  const handleClickSearch = useCallback(() => {
    switch (selectedTab) {
      case TABS.USERS:
        return searchUsers(searchText)
      case TABS.EVENTS:
        return searchEvents(searchText)
      case TABS.COMMUNITIES:
        return searchCommunities(searchText)
      case TABS.BADGES:
        return searchBadges(searchText)
      case TABS.GAMES:
        return searchGames(searchText)
    }
  }, [searchText, selectedTab, searchCommunities, searchEvents, searchUsers, searchBadges, searchGames])
  const handleChangeSearchText = useCallback(e => setSearchText(e.target.value), [])

  useEffect(() => {
    fetchCurrentUser()
  }, [])

  const handleOpenAddUserModal = () => setAddUserModal(true)
  const handleCloseAddUserModal = () => setAddUserModal(false)
  const handleOpenAddCommunityModal = () => setAddCommunityModal(true)
  const handleCloseAddCommunityModal = () => setAddCommunityModal(false)

  const handleSearchKeyPress = ({ nativeEvent: { key } }: any) => {
    if (key === "Enter") {
      handleClickSearch()
    }
  }

  return (
    <div>
      <Modal className="modal" open={addUserModal} onClose={handleCloseAddUserModal}>
        <AddUserModal fetchData={handleClickSearch} hideModal={handleCloseAddUserModal} />
      </Modal>
      <Modal className="modal" open={addCommunityModal} onClose={handleCloseAddCommunityModal}>
        <AddCommunityModal hideModal={handleCloseAddCommunityModal} />
      </Modal>
      <div className="container">
        <h2>Home</h2>
        <Tabs onChange={(_, index) => setTab(index)} selectedTab={selectedTab} />
        <div className="search-input-container">
          <TextField
            variant="outlined"
            placeholder="Enter search keys"
            className="search-input"
            onChange={handleChangeSearchText}
            size="small"
            onKeyPress={handleSearchKeyPress}
          />

          <div className="button-container">
            {isLoading ? (
              <CircularProgress size={30} />
            ) : (
              <Button variant="contained" onClick={handleClickSearch}>
                Search
              </Button>
            )}
          </div>
        </div>
        {!!data?.length && <List data={data} tab={selectedTab} fetchData={handleClickSearch} />}
      </div>
      {selectedTab === TABS.USERS && (
        <IconButton aria-label="Add" onClick={handleOpenAddUserModal} className="addButton">
          <Add />
        </IconButton>
      )}
      {selectedTab === TABS.COMMUNITIES && (
        <IconButton aria-label="Add" onClick={handleOpenAddCommunityModal} className="addButton">
          <Add />
        </IconButton>
      )}
    </div>
  )
}
